import { i18n } from 'i18n'
import { makeAutoObservable, runInAction, when } from 'mobx'
import { opt } from 'store/opt'
import { SelfieStore } from 'store/profile/SelfieStore'
import { getNextKey } from 'util/id'
import { isMobileBrowser } from 'util/mobile'

export class CameraStore {
  readonly key = getNextKey()

  private _element: HTMLVideoElement | undefined
  private _stream: MediaStream | undefined
  private _error: string | undefined

  private _isFrontCamera = true
  private _hasFrontCamera = false
  private _hasBackCamera = false

  private _starting = false
  private _processing = false

  seconds: number = 0
  timer?: TimeoutId

  constructor() {
    makeAutoObservable(this)
  }

  get element(): HTMLVideoElement | undefined {
    return this._element
  }

  set element(value: HTMLVideoElement | undefined) {
    this._element = value
  }

  get starting(): boolean {
    return this._starting
  }

  private set starting(value: boolean) {
    this._starting = value
  }

  get processing(): boolean {
    return this._processing
  }

  set processing(value: boolean) {
    this._processing = value
  }

  get canTake() {
    return !!this.stream
  }

  get isFrontCamera() {
    return this._isFrontCamera
  }

  get hasMultipleCameras() {
    if (this._hasFrontCamera && this._hasBackCamera) {
      return true
    }

    if (isMobileBrowser() && !this._hasFrontCamera && !this._hasBackCamera) {
      return true
    }

    return this._hasFrontCamera && this._hasBackCamera
  }

  private async ensureCamerasDetected() {
    if (!this._hasFrontCamera && !this._hasBackCamera) {
      await this.detectAvailableCameras()
    }
  }

  private async detectAvailableCameras() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const videoDevices = devices.filter(device => device.kind === 'videoinput')

      // Если есть больше одного видеоустройства, предполагаем наличие обеих камер
      if (videoDevices.length > 1) {
        runInAction(() => {
          this._hasFrontCamera = true
          this._hasBackCamera = true
        })
        return
      }

      // Пытаемся определить через capabilities
      for (const device of videoDevices) {
        const capabilities = (device as any).getCapabilities?.()
        if (capabilities) {
          const facingMode = capabilities.facingMode
          if (facingMode?.exact?.includes('user')) {
            this._hasFrontCamera = true
          }
          if (facingMode?.exact?.includes('environment')) {
            this._hasBackCamera = true
          }
        }
      }

      if (!this._hasFrontCamera || !this._hasBackCamera) {
        try {
          const frontStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user' },
          })
          runInAction(() => this._hasFrontCamera = true)
          frontStream.getTracks().forEach(track => track.stop())
        } catch (e) {
        }

        try {
          const backStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment' },
          })
          runInAction(() => this._hasBackCamera = true)
          backStream.getTracks().forEach(track => track.stop())
        } catch (e) {
        }
      }
    } catch (e) {
      if (isMobileBrowser()) {
        this._hasFrontCamera = true
        this._hasBackCamera = true
      }
    }
  }

  async start(): Promise<boolean> {
    this.starting = true
    try {
      this.reset()

      const element = this.element
      if (!element) return false

      await this.ensureCamerasDetected()

      element.playsInline = true
      element.autoplay = true
      element.muted = true
      element.srcObject = null

      const constraints = {
        video: {
          facingMode: this._isFrontCamera ? 'user' : 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      }

      let stream: MediaStream

      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints)
      } catch (e) {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: this._isFrontCamera ? 'user' : 'environment' },
          audio: false,
        })
      }

      this.stream = stream
      element.srcObject = stream

      await new Promise<void>((resolve, reject) => {
        const onLoadedMetadata = () => {
          element.removeEventListener('loadedmetadata', onLoadedMetadata)
          element.removeEventListener('error', onError)
          resolve()
        }

        const onError = () => {
          element.removeEventListener('loadedmetadata', onLoadedMetadata)
          element.removeEventListener('error', onError)
          reject(new Error('Video element failed to load'))
        }

        element.addEventListener('loadedmetadata', onLoadedMetadata)
        element.addEventListener('error', onError)

        setTimeout(() => {
          element.removeEventListener('loadedmetadata', onLoadedMetadata)
          element.removeEventListener('error', onError)
          reject(new Error('Video metadata loading timeout'))
        }, 5000)
      })

      await element.play()

      if (element.readyState < 2) {
        throw new Error('Video not ready after play')
      }

      return true
    } catch (e) {
      console.error('stream failed', e)
      this.reset()
      this.error = i18n('CameraNotAvailable')
      return false
    } finally {
      this.starting = false
    }
  }

  async switchCamera(): Promise<boolean> {
    this._isFrontCamera = !this._isFrontCamera
    return await this.start()
  }

  stop() {
    this.reset()
  }

  async countdown(sec = 3) {
    this.seconds = sec
    clearInterval(this.timer)
    this.timer = setInterval(() => {
      if (this.seconds > 0) runInAction(() => --this.seconds)
      else clearInterval(this.timer)
    }, 1000)
    await when(() => this.seconds === 0)
  }

  async take(selfie: SelfieStore): Promise<void> {
    this.processing = true
    try {
      let file
      try {
        file = await this.takeUnsafe()
        this.stop()
      } catch (e) {
        console.error('take failed', e)
        selfie.setError('cant_take_photo')
        return
      }
      await selfie.setFile(file).catch(() => undefined)
    } finally {
      this.processing = false
    }
  }

  private async takeUnsafe(): Promise<File> {
    if (!this.element) throw new Error('no element')
    const turn = opt.turn
    const v = (180 + turn - 45) % 180 < 90
    const w = this.element.videoWidth
    const h = this.element.videoHeight
    const elemW = this.element.clientWidth || w
    const elemH = this.element.clientHeight || h
    const scale = Math.max(elemW / w, elemH / h)
    const cropW = Math.round(elemW / scale)
    const cropH = Math.round(elemH / scale)
    const cropX = Math.max(0, Math.round((w - cropW) / 2))
    const cropY = Math.max(0, Math.round((h - cropH) / 2))
    const width = v ? cropH : cropW
    const height = v ? cropW : cropH
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) throw new Error('no context')
    context.fillStyle = '#f00'
    context.fillRect(0, 0, width, height)
    const x = width / 2
    const y = height / 2
    const a = turn * Math.PI / 180
    context.translate(x, y)
    if (opt.mirror && this.isFrontCamera) context.scale(-1, 1)
    context.rotate(-a)
    context.drawImage(this.element, cropX, cropY, cropW, cropH, -cropW / 2, -cropH / 2, cropW, cropH)
    context.rotate(a)
    context.translate(-x, -y)
    const blob = await new Promise<Blob>((r, x) => {
      canvas.toBlob(blob => {
        if (blob) r(blob)
        else x(new Error('no blob'))
      }, 'image/jpeg', 0.9)
    })
    return new File([blob], 'photo.jpg', { type: 'image/jpg' })
  }

  get stream(): MediaStream | undefined {
    return this._stream
  }

  get error(): string | undefined {
    return this._error
  }

  private set stream(value: MediaStream | undefined) {
    this._stream = value
  }

  private set error(value: string | undefined) {
    this._error = value
  }

  private reset() {
    if (this.stream) {
      for (const track of this.stream.getVideoTracks()) {
        track.stop()
      }
    }
    this.stream = undefined
    this.error = undefined
  }
}
